import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Filters,
  Page,
  PageHeader,
  SearchSelect,
  Select,
  Table,
  Toolbar,
  type Action,
} from '@evogard/ui';
import { count, loadFailureMessage } from '@evogard/domain';
import { TARGET_SCOPES } from '@evogard/contract';
import type {
  TargetScope,
  Period,
  SalesTarget,
  ReasonNotToSettle,
} from '@evogard/contract';
import { TargetCard } from './TargetCard.js';
import { TargetTrend } from './TargetTrend.js';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import type { ReasonOverrides } from './common.js';
import {
  PeriodField,
  WhyNotSettled,
  NO_ROSTER_ACCESS,
  MoneyValue,
  currentPeriod,
  periodLabel,
  useConsultants,
  useTeams,
} from './common.js';

/** Como se escreve cada alvo. Fechado no contrato, traduzido uma vez aqui. */
// @valor
const LABEL_TARGET: Record<TargetScope, string> = {
  consultor: 'Consultor',
  time: 'Time',
};

/**
 * Os dois motivos que esta tela mostra, explicados **para a meta**.
 *
 * O mapa da casa (`REASON`, em `common.tsx`) descreve os dois do ponto de vista
 * da **apuração**: fala em régua, em R$ 400,00 contra R$ 0,00, e diz que a
 * competência inteira fica sem apurar. Na meta as três coisas são falsas — ela
 * não paga nada, não há régua nenhuma nela, e a recusa é **por alvo**.
 *
 * Foi um percurso no navegador que achou: a tela de metas da GO mostrava o
 * texto da comissão ao lado de três consultores com número. Nenhum teste pega,
 * porque as duas copies estão certas cada uma no seu lugar.
 */
// @valor — as chaves são valores de `REASONS_NOT_TO_SETTLE`, dado do contrato
const WHY_THE_TARGET: ReasonOverrides = {
  venda_sem_classificacao: {
    title: 'Há venda sem a classificação da placa.',
    because:
      'A classificação diz se a placa é venda, troca, Plano Bronze ou ' +
      'reativação, e é ela que decide o que conta para a meta. Sem ela a venda ' +
      'não pode ser contada nem descartada — e adivinhar o tipo pelo texto da ' +
      'observação, como o sistema antigo fazia, é justamente o que a casa ' +
      'decidiu não fazer.',
    unlock:
      'Declarar a classificação em “Corrigir”, na tela de vendas — uma venda por ' +
      'vez, com o registro à frente de quem corrige. Só os alvos de quem tem a ' +
      'placa sem categoria ficam sem realizado, e não a competência inteira: ' +
      'aqui o número de uma pessoa não depende da placa de outra.',
  },
};

/**
 * As metas de uma competência — de consultores e de times.
 *
 * ## A meta é SEMPRE da competência, e nunca "a meta corrente"
 *
 * É a correção de um defeito medido no sistema antigo: lá a meta do mês vivo
 * mora numa coluna que a virada do mês **zera**, e a rota de bonificações lê
 * essa coluna mesmo quando perguntam por julho. O efeito é que um mês fechado
 * muda de valor sozinho, e a porta "bateu a meta pessoal" passa a ser sempre
 * aprovada — porque meta zero é meta sempre atingida.
 *
 * Aqui a competência está na chave desde o primeiro registro, e a tela obriga a
 * escolher um mês antes de mostrar qualquer coisa. Não há visão "do mês atual"
 * que mude de significado no dia 1º.
 *
 * ## O realizado SAI desde 11/09, e conta o que o perfil manda contar
 *
 * "Quantas vendas a pessoa fez" não é o realizado da meta: o realizado é
 * quantas **contam**. Para o consultor comum conta só `venda` — troca,
 * reativação e Plano Bronze ficam de fora; para quem é da base de reativação
 * conta só `reativacao`. Quem declara isso é `CONSULTANT_PROFILE_RULES`, em
 * `@evogard/domain`, e a meta de um time é a soma dos integrantes, **cada um
 * pela régua dele**.
 *
 * É uma contagem diferente da do ranking e da comissão, que contam venda +
 * troca + Bronze (P3, P5). A diferença é a regra lida, não um descuido: o mesmo
 * consultor aparece com 25 na comissão e 19 aqui, e as duas estão certas.
 *
 * **Até 11/09 esta tela dizia "a casa não sabe" em toda linha.** O motivo era
 * `venda_sem_observacoes`: o legado tirava da conta a venda cujas observações
 * dissessem "troca" e "veículo", salvo "titularidade", e não há coluna de
 * observações. O dono desfez a premissa — aquilo era como a planilha sinalizava
 * troca antes de existir coluna de tipo, e virou regra por engano de leitura.
 *
 * ## Não existe coluna "atingida", e não existe verde nesta tela
 *
 * A tela mostra o realizado e a meta, lado a lado, e **não** diz "bateu". Quem
 * compara é quem definiu a meta: meta zero é meta sempre atingida, e um selo
 * verde aqui seria o número errado saindo com a cor do acerto. Um `atingida`
 * calculado no front também seria a segunda implementação da mesma pergunta.
 *
 * ## A checagem "meta precisa declarar valor ou quantidade" é do BANCO
 *
 * Este arquivo não a repete. Quem recusa é o `check` da tabela, e o serviço
 * converte a recusa em erro de negócio com uma mensagem — que é a mensagem que
 * a tela mostra. Duas verdades divergem, e a do banco é a que vale.
 *
 * ## Editar é a mesma rota, desde 30/09/2026
 *
 * A meta já era `upsert` — definir de novo substituía —, mas a única porta era
 * o formulário do topo, que obrigava a escolher de novo o alvo e a competência
 * que a linha já mostra. A linha ganhou "Editar meta", com os dois valores
 * preenchidos e o alvo e o mês fixos: trocar o alvo seria definir outra meta,
 * não editar esta.
 *
 * ## E excluir, desde o mesmo dia — que é arquivar
 *
 * "Excluir meta" revogou o "arquivar meta não existe" de 10/09. A meta some da
 * tela e a linha fica no banco, arquivada, com os valores na trilha. Definir de
 * novo o mesmo alvo no mesmo mês a traz de volta.
 */
export function TargetsScreen() {
  const { data: context } = useSessionContext();
  const canEdit = can(context, 'sales.targets', 'editar');
  const canSeeRecord = can(context, 'sales.teams', 'ver');
  /*
   * A MESMA função que o guard usa (`canAccess`, via `can`), e não uma regra
   * escrita de novo aqui. Esconder não é proteger — quem protege é o `where` de
   * `listTargets`, que já devolveu só a própria meta. Isto decide o **desenho**:
   * uma tabela para quem conduz a praça, um cartão para quem lê o próprio
   * número.
   */
  const canSeeAny = can(context, 'sales.targets', 'ver_qualquer');

  const [period, setPeriod] = useState<Period>(currentPeriod());
  const [targetType, setTargetType] = useState<TargetScope | ''>('');
  const [setting, setSetting] = useState(false);

  const tenantId = context?.currentTenant.id;

  const targetsQuery = useQuery({
    queryKey: ['comercial', 'metas', tenantId, period, targetType],
    queryFn: async () => {
      const r = await api.sales.listTargets({
        query: { period, targetType: targetType || undefined },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'as metas desta competência'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const targets = targetsQuery.data ?? [];

  /*
   * A série do comparativo, e ela só é PEDIDA no escopo individual.
   *
   * `enabled: !canSeeAny` não é economia de requisição: é a feature inteira.
   * O comparativo é "a minha meta contra a minha venda", e quem conduz a praça
   * não tem uma — a versão agregada dela seria outro número com outro nome, e
   * desenhá-la aqui faria a figura mudar de significado conforme quem olha.
   *
   * A rota é individual por construção — não há id de alvo na entrada —, então
   * esconder aqui não é o que protege. É o que impede a tela de pedir um
   * desenho que não tem leitura para aquele leitor.
   */
  const trendQuery = useQuery({
    queryKey: ['comercial', 'metas', 'serie', tenantId, period],
    enabled: !canSeeAny && tenantId !== undefined,
    queryFn: async () => {
      const r = await api.sales.targetHistory({ query: { until: period, months: 7 } });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'o comparativo das suas metas'));
      }
      return r.body;
    },
  });

  /*
   * A união dos motivos das linhas, e não os motivos da primeira.
   *
   * Desde 11/09 as linhas **não** respondem todas a mesma coisa: a recusa é por
   * alvo, então uma competência mostra número em três metas e recusa a quarta.
   * A união é o que faz um cartão só continuar verdadeiro sobre todas — ele
   * explica o que falta a alguma delas, e a coluna diz a quais. Escolher uma
   * linha como representante faria a tela afirmar sobre as outras.
   */
  const reasons = unionOfReasons(targets);

  /* O servidor já devolve ordenado por tipo; aqui a partição é o que separa as
   * duas tabelas. Uma varredura por lista, e não um `filter` duplicado. */
  const consultants = targets.filter((m) => m.targetType === 'consultor');
  const teams = targets.filter((m) => m.targetType === 'time');

  return (
    <Page measure="cheia">
      <PageHeader
        title="Metas"
        actions={
          canEdit && <Button onClick={() => setSetting(true)}>Definir meta</Button>
        }
      >
        {canSeeAny ? (
          <>
            A meta de cada consultor e de cada time em{' '}
            <strong className="font-medium text-texto">
              {context?.currentTenant.nome}
            </strong>
            , por competência. A meta de março continua sendo a de março depois que abril
            começa.
          </>
        ) : (
          <>
            A sua meta em{' '}
            <strong className="font-medium text-texto">
              {context?.currentTenant.nome}
            </strong>
            , por competência. A meta de março continua sendo a de março depois que abril
            começa.
          </>
        )}
      </PageHeader>

      {setting && canEdit && (
        <TargetForm
          periodInitial={period}
          canSeeRecord={canSeeRecord}
          toClose={() => setSetting(false)}
        />
      )}

      {/*
       * A barra de `@evogard/ui`, e não uma `Table` com `toolbar`: o recorte
       * daqui governa DUAS tabelas — consultores e times — e o cartão da meta
       * individual. Prendê-lo a uma delas faria o filtro de uma tabela mudar a
       * outra, que é a barra mentindo sobre o próprio alcance. É a mesma
       * escolha que o relatório de demandas faz, pelo mesmo motivo.
       *
       * Sem campo de busca: esta tela não tem recorte de texto nenhum, e um
       * campo de pesquisa que não pesquisa nada é a barra prometendo o que a
       * rota não faz.
       *
       * O recorte segue o ESCOPO, e não uma preferência — nem uma permissão
       * nova. Quem vê a base inteira precisa escolher entre consultores e
       * times: são quarenta linhas de duas naturezas. Quem vê **a própria
       * meta** recebe uma linha, e um seletor de alvo sobre uma linha é um
       * campo que não recorta nada — ele oferece "times" a quem nunca terá um
       * time na lista, e "consultores" como se houvesse outros.
       */}
      <Toolbar
        toolbar={
          <Filters
            values={{ period, targetType }}
            onApply={(values) => {
              /*
               * **Vazio aqui é o mês corrente, e o campo diz isso.**
               *
               * A rota exige a competência — meta sem mês é a coluna que o
               * sistema antigo zera na virada. O painel aplica o vazio no
               * "Limpar", como em toda tela da casa, então o vazio precisa ter
               * um significado declarado em vez de ser recusado em silêncio: ele
               * devolve o recorte ao estado em que a tela nasce.
               */
              const chosen = String(values.period ?? '');
              setPeriod((chosen || currentPeriod()) as Period);
              setTargetType(String(values.targetType ?? '') as TargetScope | '');
            }}
            fields={[
              {
                type: 'periodo',
                name: 'period',
                label: 'Competência',
                help: 'Obrigatória: não existe “a meta corrente”. Vazio volta ao mês de hoje.',
              },
              canSeeAny && {
                type: 'selecao' as const,
                name: 'targetType',
                label: 'Alvo',
                placeholder: 'Consultores e times',
                options: TARGET_SCOPES.map((a) => ({
                  value: a,
                  label: LABEL_TARGET[a],
                })),
              },
            ]}
          />
        }
      />

      {targetsQuery.isError && <Alert tone="erro">{targetsQuery.error.message}</Alert>}

      {/*
       * O cartão só aparece quando ALGUMA meta recusa, e não quando há meta.
       * Até 11/09 ele era permanente, porque o realizado nunca saía; hoje ele é
       * a exceção, e mantê-lo fixo faria a tela explicar uma ausência que não
       * existe mais — acima de uma tabela cheia de números.
       *
       * Os motivos são a UNIÃO das linhas, e não os da primeira: com uma meta
       * medida ao lado de uma recusada, o cartão precisa continuar dizendo a
       * verdade sobre a segunda sem afirmar nada sobre a primeira.
       */}
      {reasons.length > 0 && (
        <WhyNotSettled
          title="Nem toda meta desta competência pôde ser medida."
          reasons={reasons}
          instead={WHY_THE_TARGET}
        >
          <p className="mt-1">
            As metas abaixo são verdadeiras — foi alguém que as digitou. O que falta é o{' '}
            <strong>realizado</strong> de parte delas, e a coluna diz quais. Nenhum número
            desta tela deve ser lido como “bateu” ou “não bateu”: quem compara com a meta
            é quem a definiu.
          </p>
        </WhyNotSettled>
      )}

      {/*
        * Sem a ação ampla: o cartão da própria meta, e nunca uma tabela de uma
        * linha. O servidor já devolveu só ela — isto é desenho, não escopo.
        */}
      {!canSeeAny && (!targetsQuery.isError || targetsQuery.data) && (
        <MyTarget
          targets={targets}
          loading={targetsQuery.isLoading}
          period={period}
        />
      )}

      {/*
       * O comparativo, abaixo do cartão. Ele não tem estado de erro próprio na
       * tela: a série é leitura de apoio, e uma segunda tarja vermelha por uma
       * figura que falhou tiraria a atenção do número, que é o que a pessoa veio
       * ver. `TargetTrend` devolve `null` com menos de duas competências.
       */}
      {!canSeeAny && trendQuery.data !== undefined && (
        <TargetTrend history={trendQuery.data} />
      )}

      {canSeeAny && (!targetsQuery.isError || targetsQuery.data) && (
        <>
          {/*
           * DUAS tabelas, e não uma com a coluna "Alvo" misturando os dois.
           *
           * Consultor e time são unidades diferentes de leitura: um é uma
           * pessoa, o outro é a soma de várias. Na mesma tabela, ordenadas por
           * tipo, a passagem de um bloco para o outro não era anunciada por
           * nada além do rótulo miúdo embaixo do nome — e a coluna "Realizado"
           * de um time, lida na linha seguinte à de um consultor, parecia o
           * número de mais uma pessoa.
           *
           * Separadas, o cabeçalho de cada cartão diz o que aquele bloco conta,
           * e a coluna do tipo deixa de existir: dentro de uma tabela de times,
           * "Time" em toda linha é tinta que não distingue nada.
           */}
          {targetType !== 'time' && (
            <TargetsTable
              title="Consultores"
              period={period}
              canEdit={canEdit}
              items={consultants}
              loading={targetsQuery.isLoading}
              loaded={targetsQuery.data !== undefined}
              empty="Nenhuma meta de consultor nesta competência. A meta é declarada mês a mês: a de março não vale para abril, e é por isso que abril começa vazio."
            />
          )}

          {targetType !== 'consultor' && (
            <TargetsTable
              title="Times"
              period={period}
              canEdit={canEdit}
              items={teams}
              loading={targetsQuery.isLoading}
              loaded={targetsQuery.data !== undefined}
              empty="Nenhuma meta de time nesta competência. A meta de um time é a soma dos integrantes, e ela também é declarada mês a mês."
            />
          )}
        </>
      )}
    </Page>
  );
}

/**
 * Um dos dois blocos da tabela: consultores, ou times.
 *
 * O mesmo corpo serve aos dois porque as colunas são as mesmas — o que muda é
 * o que a lista conta, e isso está no título. A coluna do TIPO não existe:
 * dentro de uma tabela de times, "Time" em toda linha é tinta que não
 * distingue nada.
 *
 * `loaded` é separado de `loading` de propósito. Enquanto a consulta nunca
 * respondeu, o título é "Carregando…" — e não "0 metas", que é um fato que
 * ainda não se sabe. É a mesma disciplina do esqueleto de `Metric`.
 */
function TargetsTable({
  title,
  period,
  canEdit,
  items,
  loading,
  loaded,
  empty,
}: {
  title: string;
  period: Period;
  /** `sales.targets:editar` — sem ela a linha não desenha gatilho nenhum */
  canEdit: boolean;
  items: SalesTarget[];
  loading: boolean;
  loaded: boolean;
  empty: string;
}) {
  return (
    <Card
      title={
        loaded
          ? `${title} · ${count(items.length, 'meta', 'metas')} em ${periodLabel(period)}`
          : `${title} · carregando…`
      }
      bleed
    >
      <Table
        label={`Metas de ${title.toLowerCase()}`}
        items={items}
        loading={loading}
        rowKey={(m) => String(m.id)}
        empty={empty}
        rowTitle={(m) => m.target.nome}
        rowSubtitle={() => periodLabel(period)}
        rowIcon="bandeira"
        /*
         * Lista vazia para quem não pode editar, como em Departamentos: a
         * tabela não desenha gatilho nenhum quando as ações somem. Esconder é
         * cortesia; quem nega é o guard de `PUT /sales/targets`.
         */
        rowActions={(m): Action[] =>
          canEdit
            ? [
                {
                  label: 'Editar meta',
                  icon: 'etiqueta',
                  form: (close) => <EditTargetForm target={m} onDone={close} />,
                },
                {
                  label: 'Excluir meta',
                  icon: 'fechar',
                  tone: 'perigo',
                  form: (close) => <ArchiveTargetForm target={m} onDone={close} />,
                },
              ]
            : []
        }
        columns={[
          {
            title: 'Alvo',
            minWidth: '14rem',
            /*
             * Não há selo "Arquivada": a meta excluída (arquivada, desde 30/09)
             * não chega à listagem — quem a filtra é o `where` de
             * `listTargets`. Um selo aqui nunca acenderia.
             */
            cell: (m) => <p className="truncate">{m.target.nome}</p>,
          },
          {
            title: 'Meta em dinheiro',
            alignment: 'fim',
            cell: (m) =>
              m.value === null ? (
                // nulo é "esta meta não é em dinheiro", e não "meta de R$ 0,00"
                <span className="text-texto-suave">não é em dinheiro</span>
              ) : (
                <MoneyValue value={m.value} />
              ),
          },
          {
            title: 'Meta em vendas',
            alignment: 'fim',
            cell: (m) =>
              m.quantity === null ? (
                <span className="text-texto-suave">não é em quantidade</span>
              ) : (
                <span className="tabular-nums">{m.quantity}</span>
              ),
          },
          {
            title: 'Realizado',
            minWidth: '11rem',
            alignment: 'fim',
            cell: (m) => <Achieved target={m} />,
          },
        ]}
      />
    </Card>
  );
}

/**
 * O ramo de quem vê só a própria meta.
 *
 * ## A lista vazia aqui tem DUAS causas, e elas não são a mesma
 *
 * · a pessoa **não é consultor** nesta praça — liderança, assistente, auxiliar.
 *   Ela nunca terá meta, e dizer "nenhuma meta nesta competência" a mandaria
 *   procurar um mês em que houvesse;
 * · a pessoa **é** consultora e ninguém declarou a meta dela naquele mês.
 *
 * A tela não consegue distinguir as duas — o servidor devolve `[]` nas duas —, e
 * por isso o texto diz as duas em vez de escolher uma. Inventar a mais provável
 * seria mandar metade das pessoas procurar a coisa errada, que é o mesmo
 * defeito que a recusa do cadastro já teve uma vez.
 *
 * Fechar de verdade pede o contrato dizer se quem pergunta é consultor, e isso é
 * campo novo numa rota de leitura — outra fatia.
 */
function MyTarget({
  targets,
  loading,
  period,
}: {
  targets: readonly SalesTarget[];
  loading: boolean;
  period: Period;
}) {
  if (loading) {
    return <Card title="Carregando…" />;
  }

  if (targets.length === 0) {
    return (
      <Card title={`Sua meta em ${periodLabel(period)}`}>
        <p className="text-texto-suave">
          Nenhuma meta sua nesta competência. Ou ninguém declarou a sua meta deste mês,
          ou você não é consultor nesta base — quem não vende não recebe meta, e as
          duas coisas aparecem iguais nesta tela.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {targets.map((t) => (
        <TargetCard key={t.id} target={t} />
      ))}
    </div>
  );
}

/** Os motivos de todas as linhas, sem repetir e na ordem em que apareceram. */
function unionOfReasons(targets: readonly SalesTarget[]): ReasonNotToSettle[] {
  const seen = new Set<ReasonNotToSettle>();
  for (const m of targets) {
    if (m.attainment.estado === 'nao_apuravel') {
      for (const reason of m.attainment.reasons) seen.add(reason);
    }
  }
  return [...seen];
}

/**
 * O realizado de uma linha.
 *
 * Não há verde aqui, e não há comparação com a meta — e isso não mudou quando o
 * realizado passou a sair, em 11/09: dizer "atingida" é uma pergunta com uma
 * resposta só, e meta zero é meta sempre atingida.
 *
 * O braço `nao_apuravel` continua, e continua honesto: ele aparece quando o
 * alvo tem placa sem categoria ou integrante sem perfil declarado. O que mudou
 * é que ele deixou de ser a única resposta possível — e, por isso, a célula que
 * o mostra passou a ser a exceção em vez da regra.
 *
 * A contagem é o que o perfil manda (`venda` para o comum, `reativacao` para a
 * base de reativação), então o rótulo "vendas" fica curto para quem reativa. É
 * dívida conhecida e pequena: o plural do substantivo precisa vir do perfil, e
 * hoje a coluna da meta não o transporta.
 */
function Achieved({ target }: { target: SalesTarget }) {
  if (target.attainment.estado === 'nao_apuravel') {
    return <Badge tone="informacao">A casa não sabe</Badge>;
  }
  return (
    <div>
      <p className="tabular-nums">
        {count(target.attainment.quantity, 'venda', 'vendas')}
      </p>
      <p className="mt-0.5 text-xs text-texto-suave">
        <MoneyValue value={target.attainment.value} />
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Definir
 * ------------------------------------------------------------------------- */

/**
 * Definir a meta de um alvo numa competência.
 *
 * É `upsert` na chave (base, tipo do alvo, alvo, competência): definir duas
 * vezes na mesma competência mantém **uma** linha, com o último valor. Duas abas
 * abertas na mesma meta gravam a última — e isso é o comportamento pretendido,
 * não um acidente: uma meta é uma declaração, e a última declaração é a que
 * vale.
 *
 * Os dois campos são anuláveis e nenhum tem valor sugerido. Um `0` prometido no
 * campo seria meta zero — a meta que o sistema antigo produz por acidente na
 * virada do mês, e que faz todo mundo "bater".
 */
function TargetForm({
  periodInitial,
  canSeeRecord,
  toClose,
}: {
  periodInitial: Period;
  canSeeRecord: boolean;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [targetType, setTargetType] = useState<TargetScope>('consultor');
  const [targetId, setTargetId] = useState('');
  const [period, setPeriod] = useState<Period>(periodInitial);
  const [value, setValue] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  const consultants = useConsultants({ enabled: canSeeRecord });
  const teams = useTeams({ enabled: canSeeRecord });

  const define = useMutation({
    mutationFn: async () => {
      const r = await api.sales.defineTarget({
        body: {
          targetType,
          targetId: Number(targetId),
          period,
          /*
           * Vazio vira `null` — "esta meta não é em dinheiro" —, e não `0`.
           * São coisas diferentes, e a segunda é uma meta que todo mundo bate.
           * Quem recusa a meta que não declara nenhum dos dois é o `check` do
           * banco: esta tela não repete a checagem, mostra a recusa.
           */
          value: value.trim() === '' ? null : value.trim(),
          quantity: amount.trim() === '' ? null : Number(amount),
        },
      });
      return ok(r, 200, 'Não foi possível definir a meta.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial', 'metas'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const targetsOfType =
    targetType === 'consultor'
      ? (consultants.data ?? []).map((c) => ({
          id: c.id,
          nome: `${c.user.nome} · ${c.erpVolunteerCode}`,
        }))
      : (teams.data ?? []).map((t) => ({ id: t.id, nome: t.nome }));

  return (
    <Card
      title="Definir meta"
      descricao="Definir de novo na mesma competência substitui o valor anterior — é uma linha por alvo e mês."
      actions={
        <Button variant="discreto" size="sm" onClick={toClose}>
          Cancelar
        </Button>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          define.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        {!canSeeRecord && (
          <Alert tone="alerta" title="Falta a permissão que lista os alvos.">
            {NO_ROSTER_ACCESS} Definir meta exige escolher um consultor ou um time, e a
            lista dos dois vem daquela permissão.
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Tipo de alvo"
            value={targetType}
            onChange={(e) => {
              setTargetType(e.target.value as TargetScope);
              // o alvo escolhido não vale para a outra lista: dois cadastros,
              // dois conjuntos de identificadores
              setTargetId('');
            }}
          >
            {TARGET_SCOPES.map((a) => (
              <option key={a} value={a}>
                {LABEL_TARGET[a]}
              </option>
            ))}
          </Select>

          {/*
           * Com busca pelo nome: a lista de consultores de uma praça passa de
           * cem, e rolar um `<select>` até achar uma pessoa era o gesto mais
           * lento do formulário. A busca ignora acento e casa também o código
           * do ERP, que está no rótulo.
           */}
          <SearchSelect
            key={targetType}
            label={LABEL_TARGET[targetType]}
            required
            disabled={!canSeeRecord}
            loading={targetType === 'consultor' ? consultants.isLoading : teams.isLoading}
            value={targetId}
            onChange={setTargetId}
            options={targetsOfType.map((a) => ({ value: String(a.id), label: a.nome }))}
            placeholder="Escolha…"
            searchPlaceholder={
              targetType === 'consultor' ? 'Buscar consultor pelo nome' : 'Buscar time pelo nome'
            }
            emptyMessage={
              targetType === 'consultor'
                ? 'Nenhum consultor com esse nome.'
                : 'Nenhum time com esse nome.'
            }
          />

          <PeriodField value={period} onChange={setPeriod} />

          <div />

          <Field
            label="Meta em dinheiro"
            inputMode="decimal"
            placeholder="0.00"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            help="Vazio é “esta meta não é em dinheiro” — que não é o mesmo que R$ 0,00."
          />
          <Field
            label="Meta em vendas"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            help="Vazio é “esta meta não é em quantidade”. Uma das duas precisa ser declarada."
          />
        </div>

        <div className="flex gap-2">
          <Button type="submit" loading={define.isPending} disabled={!targetId}>
            Definir
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Editar a meta de uma linha — dentro do painel de ações, como o "Renomear" de
 * Departamentos.
 *
 * O alvo e a competência NÃO são campos: vêm da linha e vão iguais. Trocar
 * qualquer um dos dois seria gravar a meta de outra chave e deixar esta como
 * estava — um "editar" que cria. Para outro alvo ou outro mês, o caminho é
 * "Definir meta", no topo.
 *
 * Os dois valores nascem preenchidos com o que está gravado, e o vazio continua
 * significando "esta meta não é nisso" — nunca zero. Esvaziar os dois é o que o
 * banco recusa (`sales_targets_declared_ck`), e a recusa aparece aqui.
 */
function EditTargetForm({ target, onDone }: { target: SalesTarget; onDone: () => void }) {
  const client = useQueryClient();
  const [value, setValue] = useState(target.value ?? '');
  const [amount, setAmount] = useState(
    target.quantity === null ? '' : String(target.quantity),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.sales.defineTarget({
        body: {
          targetType: target.targetType,
          targetId: target.target.id,
          period: target.period,
          value: value.trim() === '' ? null : value.trim(),
          quantity: amount.trim() === '' ? null : Number(amount),
        },
      });
      return ok(r, 200, 'Não foi possível editar a meta.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial', 'metas'] });
      onDone();
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}

      <p className="text-sm text-texto-suave">
        {LABEL_TARGET[target.targetType]} <strong className="font-medium text-texto">{target.target.nome}</strong>{' '}
        em {periodLabel(target.period)}. Salvar substitui os valores desta meta.
      </p>

      <Field
        label="Meta em dinheiro"
        autoFocus
        inputMode="decimal"
        placeholder="0.00"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        help="Vazio é “esta meta não é em dinheiro” — que não é o mesmo que R$ 0,00."
      />
      <Field
        label="Meta em vendas"
        inputMode="numeric"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        help="Vazio é “esta meta não é em quantidade”. Uma das duas precisa ser declarada."
      />

      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending}>
          Salvar
        </Button>
        <Button type="button" variant="contorno" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/**
 * Excluir a meta — a confirmação, dentro do painel de ações.
 *
 * Confirmação, e não o clique direto como no bloquear usuário: a meta decide
 * quem passa na porta da meta pessoal, e tirar a de um mês fechado muda o que
 * aquele mês diz. A frase nomeia o alvo, o mês e o valor que sai, para quem
 * confirma saber exatamente o que está tirando.
 *
 * Excluir é ARQUIVAR (convenção 9): a linha fica no banco, com os valores na
 * trilha, e "Definir meta" no mesmo alvo e mês a traz de volta. A frase diz
 * isso, porque "excluir" sozinho promete um ato sem volta que não é o que
 * acontece.
 */
function ArchiveTargetForm({
  target,
  onDone,
}: {
  target: SalesTarget;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const archive = useMutation({
    mutationFn: async () => {
      const r = await api.sales.archiveTarget({
        params: { id: target.id },
        body: undefined,
      });
      return ok(r, 204, 'Não foi possível excluir a meta.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comercial', 'metas'] });
      onDone();
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  const declared = [
    target.value === null ? null : <MoneyValue key="v" value={target.value} />,
    target.quantity === null ? null : count(target.quantity, 'venda', 'vendas'),
  ].filter((part) => part !== null);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        archive.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}

      <p className="text-sm text-texto">
        Excluir a meta de{' '}
        <strong className="font-medium">{target.target.nome}</strong> em{' '}
        {periodLabel(target.period)}
        {declared.length > 0 && (
          <>
            {' '}
            (
            {declared.map((part, i) => (
              <span key={i}>
                {i > 0 && ' e '}
                {part}
              </span>
            ))}
            )
          </>
        )}
        ?
      </p>
      <p className="text-sm text-texto-suave">
        A meta sai desta tela e do comparativo do consultor. Ela fica guardada no
        histórico, e definir a meta de novo para o mesmo alvo e mês a traz de volta.
      </p>

      <div className="flex gap-2">
        <Button type="submit" variant="perigo" loading={archive.isPending}>
          Excluir meta
        </Button>
        <Button type="button" variant="contorno" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
